// frontend/src/services/project/projectService.js
import api from '../api';
import errorService from '../error/errorService';

/**
 * Project Service - Handles all project-related operations
 */
class ProjectService {
  /**
   * Get all projects
   * @returns {Promise} - Promise that resolves with all projects
   */
  async getAllProjects() {
    try {
      const response = await api.get('/projects');
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch projects');
    }
  }

  /**
   * Get a project by ID
   * @param {string} projectId - The ID of the project
   * @returns {Promise} - Promise that resolves with the project
   */
  async getProjectById(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to fetch project');
    }
  }

  /**
   * Create a new project
   * @param {Object} project - Project data
   * @returns {Promise} - Promise that resolves with the new project
   */
  async createProject(project) {
    try {
      const response = await api.post('/projects', project);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to create project');
    }
  }

  /**
   * Update project title
   * @param {string} projectId - The ID of the project
   * @param {string} title - The new title
   * @returns {Promise} - Promise that resolves with the updated project
   */
  async updateTitle(projectId, title) {
    try {
      const response = await api.put(`/projects/${projectId}/title`, { title });
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to update project title');
    }
  }

  /**
   * Delete a project
   * @param {string} projectId - The ID of the project
   * @returns {Promise} - Promise that resolves when project is deleted
   */
  async deleteProject(projectId) {
    try {
      const response = await api.delete(`/projects/${projectId}`);
      return response.data;
    } catch (error) {
      return errorService.handleApiError(error, 'Failed to delete project');
    }
  }

  /**
   * Get the glossary for a project
   * @param {string} projectId - The ID of the project
   * @returns {Promise} - Promise that resolves with the glossary
   */
  async getGlossary(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/glossary`);
      return response.data?.terms || [];
    } catch (error) {
      console.error("Error fetching glossary:", error);
      return []; // Return empty glossary on error to avoid breaking translation
    }
  }

  /**
   * Get the instructions for a project
   * @param {string} projectId - The ID of the project
   * @returns {Promise<string>} - Promise that resolves with the instructions
   */
  async getInstructions(projectId) {
    try {
      const response = await api.get(`/projects/${projectId}/instructions`);
      return response.data?.text || '';
    } catch (error) {
      console.error("Error fetching instructions:", error);
      return ''; // Return empty instructions on error
    }
  }
}

export default new ProjectService();